import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { NoveltyCheckReportContext } from '../novelty-check-report-context';
import type { NoveltyCheckReportState } from '../novelty-check-report-state';
import { Logger } from '@patsnap/tahiti-core';
import { NoveltyCheckTaskStep } from '@/domains/novelty-check-report/enums';
import { FeatureIdentify } from '../../feature-identify';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { NoveltyCheckTaskStatusModel } from '@/domains/novelty-check-report/models/novelty-check-task-status-model';
import { NoveltyCheckTaskStatusService } from '../../task-status-service';
import { RetrievalResultReportModel } from '@/domains/novelty-check-report/models/retrieval-result-report-model';
import { AdjustTechFeaturesService } from '../../adjust-tech-features-service';
import { NoveltyCheckReportTitleService, TechFeaturesService } from '../../retrieval-result-report-service';
import { SummaryTechFeaturesService } from '../../summary-tech-features-service';

@injectable()
export class NoveltySearchTechFeatureState implements NoveltyCheckReportState {
    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FeatureIdentify)
        private readonly _featureIdentify: FeatureIdentify,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(NoveltyCheckTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: NoveltyCheckTaskStatusModel,
        @inject(NoveltyCheckTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: NoveltyCheckTaskStatusService,
        @inject(SummaryTechFeaturesService)
        private readonly _summaryTechFeatureService: SummaryTechFeaturesService,
        @inject(NoveltyCheckReportTitleService)
        private readonly _noveltyCheckReportTitleService: NoveltyCheckReportTitleService,
        @inject(TechFeaturesService)
        private readonly _techFeaturesService: TechFeaturesService,
        @inject(AdjustTechFeaturesService)
        private readonly _adjustTechFeaturesService: AdjustTechFeaturesService,
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
    ) { }

    async enter(context: NoveltyCheckReportContext): Promise<void> {
        await this._noveltyCheckTaskStatusService.pullTaskStatus();
        this._logger.info('进入<技术特征识别>状态');
        // 切换任务到技术特征识别状态
        this._switchTaskStep(NoveltyCheckTaskStep.NOVELTY_SEARCH_TECH_FEATURE);

        try {
            // 识别技术特征
            await this._recognizeTechFeature();

            // 获取技术特征
            this._techFeaturesService.pullTechFeatures();

            this._logger.info('技术特征识别成功');
        } catch (e) {
            this._logger.error('技术特征识别失败', e);
        }
    }

    /**
     * 处理技术特征识别失败，手动进入编辑状态
     */
    async handleFailure(context: NoveltyCheckReportContext): Promise<void> {
        this._logger.info('手动进入编辑技术要点状态');
        const searchInputState = context.getState(NoveltyCheckTaskStep.NOVELTY_SEARCH_INPUT);
        await context.transitionTo(searchInputState);
    }

    /**
     * 识别技术特征
     */
    private _recognizeTechFeature() {
        const taskId = this._currentTaskService.taskId;
        return this._featureIdentify.recognize(taskId);
    }

    /**
     * 切换任务步骤
     * @param step 目标步骤
     */
    private _switchTaskStep(step: NoveltyCheckTaskStep) {
        this._noveltyCheckTaskStatusModel.update(state => {
            state.taskStep = step;
            return state;
        });
    }

    async handleNext(context: NoveltyCheckReportContext): Promise<void> {
        await this._actionsBeforeNext();
        // 从技术特征识别状态进入检索要素识别状态
        const retrievalElementIdentifyStartState = context.getState(NoveltyCheckTaskStep.NOVELTY_SEARCH_RETRIEVAL_ELEMENTS);
        context.transitionTo(retrievalElementIdentifyStartState);
    }

    private async _actionsBeforeNext() {
        // 获取已经识别的技术特征
        const techFeatures = await this._getTechFeatures();
        await this._adjustTechFeaturesService.adjust(techFeatures);
    }

    // 获取已经识别的技术特征
    private async _getTechFeatures() {
        const techFeatures = this._retrievalResultReportModel.modelData.techFeatures;
        return techFeatures;
    }

    async handlePrevious(context: NoveltyCheckReportContext): Promise<void> {
        // 返回到 SUMMARY 状态
        const summaryState = context.getState(NoveltyCheckTaskStep.SUMMARY);
        await context.transitionTo(summaryState);
    }
}
